import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { useNavigate } from "react-router-dom";
import { Sparkles, Loader2, Send, Trophy, Lightbulb, Flag, Target, X } from "lucide-react";
import { toast, Toaster } from "sonner";

const START_POINTS = 100;
const MAX_ATTEMPTS = 5;

export default function Audrey() {
  const { user } = useAuth();
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState(null);
  const [description, setDescription] = useState(null);
  const [hintsTotal, setHintsTotal] = useState(0);
  const [hints, setHints] = useState([]);
  const [guess, setGuess] = useState("");
  const [history, setHistory] = useState([]);
  const [attemptsUsed, setAttemptsUsed] = useState(0);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [finished, setFinished] = useState(false);
  const [finalScore, setFinalScore] = useState(null);
  const [revealAnswer, setRevealAnswer] = useState(null);
  const [lastCorrect, setLastCorrect] = useState(false);
  const [busy, setBusy] = useState(false);

  const resetState = () => {
    setSessionId(null); setDescription(null); setHintsTotal(0); setHints([]);
    setGuess(""); setHistory([]); setAttemptsUsed(0); setHintsUsed(0);
    setFinished(false); setFinalScore(null); setRevealAnswer(null); setLastCorrect(false);
  };

  const start = async () => {
    setBusy(true);
    resetState();
    try {
      const { data } = await api.post("/audrey/start", { lang });
      setSessionId(data.session_id);
      setDescription(data.description);
      setHintsTotal(data.hints_total || 0);
    } catch (e) {
      const msg = e?.response?.data?.detail || t("audrey.toastBusy");
      toast.error(String(msg));
    } finally { setBusy(false); }
  };

  const askHint = async () => {
    if (!sessionId || finished || hintsUsed >= hintsTotal) return;
    setBusy(true);
    try {
      const { data } = await api.post("/audrey/hint", { session_id: sessionId, lang });
      setHints((h) => [...h, data.hint]);
      setHintsUsed(data.hints_used);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("audrey.toastNoHint"));
    } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!guess.trim() || !sessionId || finished) return;
    setBusy(true);
    const g = guess;
    try {
      const { data } = await api.post("/audrey/guess", { session_id: sessionId, guess: g, lang });
      setHistory((h) => [...h, { guess: g, correct: data.correct, reply: data.reply }]);
      setAttemptsUsed(data.attempts_used);
      setLastCorrect(!!data.correct);
      setGuess("");
      if (data.finished) {
        setFinished(true);
        setFinalScore(data.final_score ?? 0);
        setRevealAnswer(data.answer || null);
        if (data.correct) toast.success(t("audrey.toastBravo").replace("{n}", data.final_score));
        else toast.error(t("audrey.toastOver"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("audrey.toastError"));
    } finally { setBusy(false); }
  };

  const giveUp = async () => {
    if (!sessionId || finished) return;
    if (!window.confirm(t("audrey.giveUpConfirm"))) return;
    setBusy(true);
    try {
      const { data } = await api.post("/audrey/give-up", { session_id: sessionId, lang });
      setFinished(true);
      setFinalScore(data.final_score ?? 0);
      setRevealAnswer(data.answer || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("audrey.toastError"));
    } finally { setBusy(false); }
  };

  const attemptsLeft = Math.max(0, MAX_ATTEMPTS - attemptsUsed);
  const potentialScore = Math.max(10, START_POINTS - attemptsUsed * 20 - hintsUsed * 10);

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-10 py-12 md:py-16" data-testid="audrey-page">
      <Toaster theme="light" />
      <div className="flex items-center gap-4 mb-8">
        <img src="/brand/audrey-ai.png" alt="geoVINUM AI" className="w-16 h-16 rounded-full object-cover ring-2 ring-[#E5276A]/30" data-testid="audrey-avatar" />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#E5276A] font-bold">{t("audrey.brand")}</div>
          <h1 className="font-display text-3xl text-[#1F3A5F]">{t("audrey.title")}</h1>
          <p className="text-sm text-[#8B95A1]">{t("audrey.subtitle")}</p>
        </div>
      </div>

      {!description && (
        <div className="app-card p-8 text-center" data-testid="audrey-empty">
          <img src="/brand/audrey-ai.png" alt="geoVINUM AI" className="w-20 h-20 rounded-full object-cover mx-auto ring-4 ring-[#E5276A]/20" />
          <p className="text-[#4A5568] mt-4 max-w-xl mx-auto">{t("audrey.intro")}</p>
          <div className="mt-4 inline-flex flex-wrap items-center justify-center gap-2 text-xs text-[#8B95A1]">
            <span className="px-2 py-1 rounded-full bg-[#FCE4ED] text-[#E5276A] font-bold">{t("audrey.badgeMaxPts")}</span>
            <span>·</span>
            <span>{t("audrey.badgeAttempts").replace("{n}", MAX_ATTEMPTS)}</span>
            <span>·</span>
            <span>{t("audrey.badgeBonus")}</span>
          </div>
          <button onClick={start} disabled={busy} data-testid="audrey-start" className="mt-5 btn-primary">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {t("audrey.startBtn")}
          </button>
        </div>
      )}

      {description && (
        <div className="space-y-5" data-testid="audrey-game">
          <div className="app-card p-4 grid grid-cols-3 gap-3" data-testid="audrey-status">
            <Stat icon={Target} label={t("audrey.stat.attempts")} value={`${attemptsUsed} / ${MAX_ATTEMPTS}`} sub={t("audrey.stat.attemptsLeft").replace("{n}", attemptsLeft)} />
            <Stat icon={Lightbulb} label={t("audrey.stat.hints")} value={`${hintsUsed} / ${hintsTotal}`} sub={t("audrey.stat.hintsCost")} />
            <Stat icon={Trophy} label={t("audrey.stat.score")} value={finished ? `${finalScore} pts` : `~${potentialScore} pts`} sub={finished ? (lastCorrect ? t("audrey.stat.scoreFound") : t("audrey.stat.scoreFinished")) : ""} tone={finished ? (lastCorrect ? "success" : "muted") : "primary"} />
          </div>

          <div className="app-card p-6">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#E5276A] font-bold mb-2">{t("audrey.cluesHeader")}</div>
            <p className="text-[#1F3A5F] leading-relaxed italic">{description}</p>
          </div>

          {hints.length > 0 && (
            <div className="space-y-2">
              {hints.map((h, i) => (
                <div key={i} data-testid={`audrey-hint-${i}`} className="app-card p-4 bg-[#FCE4ED]/40 text-sm text-[#1F3A5F] flex items-start gap-2">
                  <Lightbulb className="w-4 h-4 text-[#E5276A] shrink-0 mt-0.5" />
                  <div><strong>{t("audrey.hintLabel")} {i + 1} :</strong> {h}</div>
                </div>
              ))}
            </div>
          )}

          {history.length > 0 && (
            <div className="space-y-2" data-testid="audrey-history">
              {history.map((h, i) => (
                <div key={i} className={`app-card p-4 border-l-4 ${h.correct ? "border-[#22C55E]" : "border-[#E5276A]"}`} data-testid={`audrey-attempt-${i}`}>
                  <div className="flex items-start gap-3">
                    <img src="/brand/audrey-ai.png" alt="" className="w-8 h-8 rounded-full object-cover ring-2 ring-[#E5276A]/30 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-[10px] uppercase tracking-wider text-[#8B95A1] font-bold">{t("audrey.attemptLabel")}</span>
                        <span className="text-sm font-bold text-[#1F3A5F]">« {h.guess} »</span>
                        {h.correct
                          ? <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#DCFCE7] text-[#15803D]">{t("audrey.correctTag")}</span>
                          : <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FCE4ED] text-[#C01661]">{t("audrey.incorrectTag")}</span>}
                      </div>
                      <p className="text-[#1F3A5F] whitespace-pre-line text-sm leading-relaxed">{h.reply}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!finished && (
            <div className="space-y-3">
              <form onSubmit={submit} className="app-card p-2 flex items-center gap-2" data-testid="audrey-guess-form">
                <input
                  data-testid="audrey-guess"
                  value={guess}
                  onChange={(e) => setGuess(e.target.value)}
                  placeholder={attemptsUsed === 0 ? t("audrey.inputPlaceholderFirst") : t("audrey.inputPlaceholderNext")}
                  className="flex-1 bg-transparent outline-none px-3 py-2 text-sm text-[#1F3A5F]"
                  disabled={busy}
                />
                <button disabled={busy || !guess.trim()} data-testid="audrey-submit"
                  className="btn-primary disabled:opacity-50 inline-flex items-center gap-1.5">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {t("audrey.submit")}
                </button>
              </form>
              <div className="flex flex-wrap gap-2">
                {hintsUsed < hintsTotal && (
                  <button onClick={askHint} disabled={busy} data-testid="audrey-hint"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E5276A]/40 text-xs font-bold text-[#E5276A] hover:bg-[#FCE4ED] transition-colors">
                    <Lightbulb className="w-3.5 h-3.5" /> {t("audrey.askHint")} <span className="opacity-60">{t("audrey.hintCost")}</span>
                  </button>
                )}
                <button onClick={giveUp} disabled={busy} data-testid="audrey-giveup"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#1F3A5F]/15 text-xs font-bold text-[#8B95A1] hover:text-[#E5276A] hover:border-[#E5276A]/30 transition-colors">
                  <Flag className="w-3.5 h-3.5" /> {t("audrey.giveUp")} <span className="opacity-60">{t("audrey.giveUpEnd")}</span>
                </button>
              </div>
            </div>
          )}

          {finished && (
            <div className={`app-card p-6 ${lastCorrect ? "border-l-4 border-[#22C55E]" : "border-l-4 border-[#E5276A]"}`} data-testid="audrey-final">
              <div className="flex items-start gap-4">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${lastCorrect ? "bg-[#DCFCE7]" : "bg-[#FCE4ED]"}`}>
                  {lastCorrect ? <Trophy className="w-7 h-7 text-[#15803D]" /> : <X className="w-7 h-7 text-[#C01661]" />}
                </div>
                <div className="flex-1">
                  <div className="font-display text-2xl text-[#1F3A5F]">
                    {lastCorrect ? t("audrey.finalCongrats") : t("audrey.finalOver")}
                  </div>
                  <div className="text-sm text-[#4A5568] mt-1">
                    {t("audrey.finalScore")} : <strong className="text-[#E5276A]" data-testid="audrey-final-score">{finalScore} pts</strong> ·
                    {" "}{attemptsUsed} {attemptsUsed > 1 ? t("audrey.attempts") : t("audrey.attempt")} · {hintsUsed} {hintsUsed > 1 ? t("audrey.hints") : t("audrey.hint")}
                  </div>
                  {revealAnswer ? (
                    <div className="mt-4 p-4 rounded-xl bg-gradient-to-br from-[#FBF7F3] to-[#FCE4ED]/40 border-2 border-[#E5276A]/20" data-testid="audrey-reveal">
                      <div className="text-[10px] uppercase tracking-wider text-[#E5276A] font-bold mb-1.5">{t("audrey.answerHeader")}</div>
                      <div className="font-display text-2xl text-[#1F3A5F]" data-testid="audrey-reveal-appellation">
                        {revealAnswer.appellation}
                        {revealAnswer.color ? <span className="text-[#E5276A]"> · {revealAnswer.color}</span> : null}
                      </div>
                      <div className="text-sm text-[#4A5568] mt-2">
                        <strong className="text-[#1F3A5F]">{t("audrey.region")} :</strong> {revealAnswer.region}{revealAnswer.country ? `, ${revealAnswer.country}` : ""}
                        {revealAnswer.main_grapes?.length ? <><br /><strong className="text-[#1F3A5F]">{t("audrey.grapes")} :</strong> {revealAnswer.main_grapes.join(", ")}</> : null}
                        {revealAnswer.style ? <><br /><strong className="text-[#1F3A5F]">{t("audrey.style")} :</strong> {revealAnswer.style}</> : null}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <button onClick={start} disabled={busy} data-testid="audrey-replay" className="btn-primary inline-flex items-center gap-1.5">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {t("audrey.replayBtn")}
                </button>
                <button onClick={() => navigate("/play")} className="px-4 py-2 rounded-full border border-[#1F3A5F]/15 text-sm font-bold text-[#1F3A5F] hover:border-[#E5276A]">
                  {t("audrey.otherGames")}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, tone = "navy" }) {
  const valCls = tone === "success" ? "text-[#15803D]" : tone === "primary" ? "text-[#E5276A]" : tone === "muted" ? "text-[#8B95A1]" : "text-[#1F3A5F]";
  return (
    <div className="text-center">
      <div className="flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-wider text-[#8B95A1] font-bold mb-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`font-display text-xl ${valCls}`}>{value}</div>
      {sub && <div className="text-[10px] text-[#8B95A1] mt-0.5">{sub}</div>}
    </div>
  );
}
